import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTheme } from '@emotion/react';
import PropTypes from 'prop-types';
import { css, StyleSheet } from 'aphrodite/no-important';
import { v4 as uuidv4 } from 'uuid';
import { PopupMenu } from '../popup-menu';
import { ThemeContext } from '../theme';

const _daysInMonth = (month, year) => {
  month = month + 1;
  const isLeapYear = year % 4 === 0;
  const noOfDays = {
    1: 31,
    2: 28,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31
  };

  const days = noOfDays[month];
  if (month === 2 && isLeapYear) {
    return days + 1;
  }
  return days;
};

const _monthNames = {
  0: 'January',
  1: 'Feburary',
  2: 'March',
  3: 'April',
  4: 'May',
  5: 'June',
  6: 'July',
  7: 'August',
  8: 'September',
  9: 'October',
  10: 'November',
  11: 'December'
};

const props = {
  date: PropTypes.number,
  month: PropTypes.number,
  year: PropTypes.number,
  color: PropTypes.string,
  showPristineState: PropTypes.bool,
  left: PropTypes.number,
  right: PropTypes.number,
  onChangeDate: PropTypes.func,
  readOnlyMode: PropTypes.bool,
  isDisabled: PropTypes.bool
};

const defaultProps = {
  color: null,
  date: null,
  month: null,
  year: null,
  left: 8,
  readOnlyMode: false,
  showPristineState: false,
  isDisabled: false
};

const DatePicker = (props) => {
  const dateLabel = useRef();
  const context = useContext(ThemeContext);
  const theme = useTheme();
  let datePicker;
  if (theme && theme.datePicker) {
    datePicker = theme.datePicker;
  } else {
    datePicker = context.theme.datePicker;
  }
  const {
    year: yearProp,
    month: monthProp,
    date: dateProp,
    color,
    showPristineState,
    left,
    right,
    isPrisitine = false,
    noUnderline = false
  } = props;

  const yCounter = Math.floor((((new Date()).getFullYear()) - props.year) / 25) * 25;
  const [isDateMenuVisible, setDateMenuVisibility] = useState(false);
  const [isYearMenuVisible, setYearMenuVisibility] = useState(false);
  const [isMonthMenuVisible, setMonthMenuVisibility] = useState(false);
  const [yearsCounter, setYearsCounter] = useState(yCounter);
  const [year, setYear] = useState(props.year);
  const [month, setMonth] = useState(props.month);
  const [date, setDate] = useState(props.date);
  const [isInPristineState, setPristineState] = useState(isPrisitine);

  useEffect(() => {
    setPristineState(props.isPrisitine);
  }, [props.isPrisitine]);

  const toggleDateMenu = () => {
    if (!props.readOnlyMode && !props.isDisabled) {
      setDateMenuVisibility(!isDateMenuVisible);
      setMonthMenuVisibility(false);
      setYearMenuVisibility(false);
      setYearsCounter(yCounter);
      setYear(props.year);
      setMonth(props.month);
      setDate(props.date);
    }
  };

  const handleOutsideClick = () => {
    setDateMenuVisibility(false);
  };

  const handleMonthIncrement = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleMonthDecrement = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleYearIncrement = () => {
    setYear(year + 1);
  };

  const handleYearDecrement = () => {
    setYear(year - 1);
  };

  const handleOnDateClick = (date) => {
    setDate(date);
    setDateMenuVisibility(false);
    setYearMenuVisibility(false);
    setMonthMenuVisibility(false);
    setPristineState(false);
    props.onChangeDate(year, month, date);
  };

  const handleOnMonthClick = (month) => {
    setMonth(month);
    setMonthMenuVisibility(false);
  };

  const handleOnYearClick = (year) => {
    setYear(year);
    setYearMenuVisibility(false);
  };

  const handleOnClickToday = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const currentDate = now.getDate();

    setYear(currentYear);
    setMonth(currentMonth);
    setDate(currentDate);
    setDateMenuVisibility(false);
    setPristineState(false);
    props.onChangeDate(currentYear, currentMonth, currentDate);
  };

  const handleOnIncrementYearsMenu = () => {
    setYearsCounter(yearsCounter - 25);
  };

  const handleOnDecrementYearsMenu = () => {
    setYearsCounter(yearsCounter + 25);
  };

  const {
    showCircledBorder,
    dateLabelColor,
    dateLabelBorderColor,
    background,
    headerBgColor,
    headerTextColor,
    headerBorderColor,
    headerNavBtnBgColor,
    headerNavBtnTextColor,
    monthHeaderBg,
    monthHeaderTextColor,
    yearHeaderBg,
    yearHeaderTextColor,
    activeMonthHeaderBg,
    activeMonthHeaderTextColor,
    activeYearHeaderBg,
    activeYearHeaderTextColor,
    footerBgColor,
    footerTextColor,
    footerBorderColor,
    itemBgColor,
    itemTextColor,
    itemSecondaryTextColor,
    itemHoverBgColor,
    itemHoverTextColor,
    currentItemBgColor,
    currentItemTextColor,
    currentItemHoverBgColor,
    currentItemHoverTextColor,
    currentItemBorderColor,
    selectedItemBgColor,
    selectedItemTextColor,
    selectedItemHoverBgColor,
    selectedItemHoverTextColor,
    selectedItemBorderColor,
    currentItemHoverBorderColor,
    selectedItemHoverBorderColor,
  } = datePicker;

  const styles = StyleSheet.create({
    dateLabelParent: {
      // width: '70px',
      height: '20px',
      color: color || dateLabelColor,
      position: 'relative'
    },
    label: {
      // width: '70px',
      height: '20px',
      borderBottom: noUnderline ? '' : `1px solid ${dateLabelBorderColor}`,
      cursor: 'pointer'
    },
    datePickerMenu: {
      width: '320px',
      background: background,
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)'
    },
    datePickerMenuHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '40px',
      padding: '0 4px',
      borderBottom: `1px solid ${headerBorderColor}`,
      background: headerBgColor,
      color: headerTextColor
    },
    calNavBtn: { // Header calendar navigation buttons
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      background: headerNavBtnBgColor,
      color: headerNavBtnTextColor,
      width: '30px',
      height: '38px',
      fontSize: '12px'
    },
    datePickerMenuFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      height: '40px',
      width: '320px',
      borderTop: `1px solid ${footerBorderColor}`,
      color: footerTextColor,
      background: footerBgColor
    },
    datePickerCalendar: { // Date menu container
      width: '320px',
      padding: '8px'
    },
    weekDaysHeader: {
      display: 'flex',
      alignItems: 'center'
    },
    weekDaysCells: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '42px',
      minHeight: '42px',
      color: itemTextColor,
      ':first-child': {
        color: itemSecondaryTextColor
      },
      ':last-child': {
        color: itemSecondaryTextColor
      }
    },
    calendarCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '40px',
      minHeight: '40px',
      cursor: 'pointer',
      background: itemBgColor,
      color: itemTextColor,
      borderRadius: showCircledBorder ? '50%' : '5px',
      ':first-child': {
        color: itemSecondaryTextColor
      },
      ':last-child': {
        color: itemSecondaryTextColor
      },
      ':hover': {
        background: itemHoverBgColor,
        color: itemHoverTextColor
      }
    },
    currentCalendarCell: {
      background: currentItemBgColor,
      color: currentItemTextColor,
      border: `1px solid ${currentItemBorderColor}`,
      borderRadius: showCircledBorder ? '50%' : '5px',
      ':hover': {
        background: currentItemHoverBgColor,
        color: currentItemHoverTextColor
      }
    },
    activeCalendarCell: {
      background: selectedItemBgColor,
      color: selectedItemTextColor,
      border: `1px solid ${selectedItemBorderColor}`,
      borderRadius: showCircledBorder ? '50%' : '5px',
      ':hover': {
        background: selectedItemHoverBgColor,
        color: selectedItemHoverTextColor
      }
    },
    monthHeader: {
      background: monthHeaderBg,
      color: monthHeaderTextColor,
      cursor: 'pointer',
      fontSize: '14px',
      borderRadius: '5px',
      height: '32px',
      padding: '6px 8px',
      marginRight: '4px'
    },
    activeMonthHeader: {
      background: activeMonthHeaderBg,
      color: activeMonthHeaderTextColor
    },
    monthMenu: {
      background: background,
      width: '320px'
    },
    monthCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      width: '85px',
      height: '50px',
      borderRadius: '5px',
      color: itemTextColor,
      background: itemBgColor,
      ':hover': {
        background: itemHoverBgColor
      }
    },
    selectedMonthCell: {
      background: selectedItemBgColor,
      color: selectedItemTextColor,
      ':hover': {
        background: selectedItemHoverBgColor,
        color: selectedItemHoverTextColor
      }
    },
    yearHeader: {
      background: yearHeaderBg,
      color: yearHeaderTextColor,
      cursor: 'pointer',
      fontSize: '14px',
      borderRadius: '5px',
      height: '32px',
      padding: '6px 8px'
    },
    activeYearHeader: {
      background: activeYearHeaderBg,
      color: activeYearHeaderTextColor
    },
    yearsMenu: {
      background: background,
      display: 'flex'
    },
    yearsContainer: {
      display: 'grid',
      gridTemplateColumns: 'auto auto auto auto auto',
      padding: '10px 0',
      width: '272px'
    },
    yearNavContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '270px'
    },
    yearNavBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: itemHoverBgColor,
      color: itemTextColor,
      width: '24px',
      height: '46px',
      cursor: 'pointer'
    },
    yearCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      width: '50px',
      height: '50px',
      background: itemBgColor,
      color: itemTextColor,
      borderRadius: '5px',
      ':hover': {
        background: itemHoverBgColor,
        color: itemHoverTextColor
      }
    },
    selectedYearCell: {
      background: selectedItemBgColor,
      color: selectedItemTextColor,
      ':hover': {
        background: selectedItemHoverBgColor,
        color: selectedItemHoverTextColor
      }
    }
  });

  const newDate = new Date(year, month, date);
  const noOfDays = _daysInMonth(newDate.getMonth(), newDate.getYear());
  const firstDayDate = new Date(year, month, 1);
  const firstDay = firstDayDate.getDay();
  const noOfRows = firstDay <= 4 ? 5 : 6;
  const rows = [];

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const currentDate = now.getDate();

  // Generating calendar
  for (let i = 0; i < noOfRows; i++) {
    const tmp = new Array(7);
    if (i === 0) {
      for (let j = firstDay; j < 7; j++) {
        const d = j - firstDay + 1;
        tmp[j] = d;
      }
      rows.push(tmp);
    } else {
      for (let j = 0; j < 7; j++) {
        const addFactor = 6 - firstDay + 2 + (7 * (i - 1));
        const d = j + addFactor;
        if (d <= noOfDays) {
          tmp[j] = d;
        }
      }
      rows.push(tmp);
    }
  }

  // Calendar
  const dates = [];
  rows.forEach(row => {
    const tmp = [];
    for (let i = 0; i < row.length; i++) {
      if (row[i]) {
        tmp.push(
          <div
            key={uuidv4()}
            className={`mb-1 mr-1 ${css(
              styles.calendarCell,
              (month === currentMonth && year === currentYear && row[i] === currentDate) && styles.currentCalendarCell,
              (month === monthProp && year === yearProp && row[i] === dateProp) && styles.activeCalendarCell
            )}`}
            onClick={() => {
              handleOnDateClick(row[i]);
            }}
          >
            {row[i]}
          </div>
        );
      } else {
        tmp.push(
          <div
            style={{ minWidth: '42px', minHeight: '42px' }}
            key={uuidv4()}
          >
            {' '}
          </div>
        );
      }
    }
    dates.push(
      <div
        style={{ display: 'flex' }}
        key={uuidv4()}
      >
        {tmp}
      </div>
    );
  });

  const calendar = (
    <div className={css(styles.datePickerCalendar)}>
      {/* Days of week top row */}
      <div className={css(styles.weekDaysHeader)}>
        <div className={`noselect ${css(styles.weekDaysCells)}`}>Sun</div>
        <div className={`noselect ${css(styles.weekDaysCells)}`}>Mon</div>
        <div className={`noselect ${css(styles.weekDaysCells)}`}>Tue</div>
        <div className={`noselect ${css(styles.weekDaysCells)}`}>Wed</div>
        <div className={`noselect ${css(styles.weekDaysCells)}`}>Thu</div>
        <div className={`noselect ${css(styles.weekDaysCells)}`}>Fri</div>
        <div className={`noselect ${css(styles.weekDaysCells)}`}>Sat</div>
      </div>
      {/* Monthly dates */}
      {dates}
    </div>
  );

  // Months menu
  const monthsMenu = (
    <div className={css(styles.monthMenu)}>
      <div className='fx fx__hv-center mb-3 mt-3'>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 0 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(0) }}
        >
          January
        </div>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 1 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(1) }}
        >
          Feburary
        </div>
        <div
          className={css(styles.monthCell, monthProp === 2 && styles.selectedMonthCell)}
          onClick={() => { handleOnMonthClick(2) }}
        >
          March
        </div>
      </div>
      <div className='fx fx__hv-center mb-3'>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 3 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(3) }}
        >
          April
        </div>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 4 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(4) }}
        >
          May
        </div>
        <div
          className={css(styles.monthCell, monthProp === 5 && styles.selectedMonthCell)}
          onClick={() => { handleOnMonthClick(5) }}
        >
          June
        </div>
      </div>
      <div className='fx fx__hv-center mb-3'>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 6 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(6) }}
        >
          July
        </div>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 7 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(7) }}
        >
          August
        </div>
        <div
          className={css(styles.monthCell, monthProp === 8 && styles.selectedMonthCell)}
          onClick={() => { handleOnMonthClick(8) }}
        >
          September
        </div>
      </div>
      <div className='fx fx__hv-center mb-3'>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 9 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(9) }}
        >
          October
        </div>
        <div
          className={`mr-3 ${css(styles.monthCell, monthProp === 10 && styles.selectedMonthCell)}`}
          onClick={() => { handleOnMonthClick(10) }}
        >
          November
        </div>
        <div
          className={css(styles.monthCell, monthProp === 11 && styles.selectedMonthCell)}
          onClick={() => { handleOnMonthClick(11) }}
        >
          December
        </div>
      </div>
    </div>
  );

  // Years menu and cells
  const years = [];
  for (let i = 0; i < 25; i++) {
    const year = currentYear - yearsCounter - i;
    years.push(
      <div
        className={css(styles.yearCell, year === yearProp && styles.selectedYearCell)}
        key={uuidv4()}
        onClick={() => { handleOnYearClick(currentYear - yearsCounter - i); }}
      >
        {year}
      </div>
    );
  }

  const yearsMenu = (
    <div className={css(styles.yearsMenu)}>
      {/* Decrement years */}
      <div className={css(styles.yearNavContainer)}>
        <div
          className={css(styles.yearNavBtn)}
          onClick={handleOnDecrementYearsMenu}
        >
          <i className='fa fa-chevron-left' />
        </div>
      </div>
      {/* Years */}
      <div className={css(styles.yearsContainer)}>
        {years}
      </div>
      {/* Increment years */}
      <div className={css(styles.yearNavContainer)}>
        <div
          className={css(styles.yearNavBtn)}
          onClick={handleOnIncrementYearsMenu}
        >
          <i className='fa fa-chevron-right' />
        </div>
      </div>
    </div>
  );

  // Decide which menu to show
  let menu = calendar;
  if (isMonthMenuVisible) {
    menu = monthsMenu;
  }

  if (isYearMenuVisible) {
    menu = yearsMenu;
  }

  let datePickerTop = 25;
  // was -> 356
  if (dateLabel.current && window.innerHeight - dateLabel.current.getBoundingClientRect().top >= 400) { // was 475
    datePickerTop = 25;
  } else {
    // datePickerTop = -402;
    datePickerTop = -364;
  }

  return (
    <div className={`${props.className || ''} ${css(styles.dateLabelParent)}`}>
      <div
        className={css(styles.label)}
        onClick={toggleDateMenu}
        ref={dateLabel}
      >
        {/* {(isInPristineState || showPristineState) ? 'mm/dd/yyyy' : `${('0' + (monthProp + 1)).slice(-2)}/${('0' + dateProp).slice(-2)}/${yearProp}`} */}
        {isInPristineState ? 'mm/dd/yyyy' : `${('0' + (monthProp + 1)).slice(-2)}/${('0' + dateProp).slice(-2)}/${yearProp}`}
      </div>
      {/* top was 25 */}
      {isDateMenuVisible
        ? <PopupMenu
            top={datePickerTop}
            left={left}
            right={right}
            position='absolute'
            visible={isDateMenuVisible}
            parent={dateLabel}
            onOutsideClick={handleOutsideClick}
            zIndex={props.zIndex || '1450'}
          >
            <div className={css(styles.datePickerMenu)}>
              {/* Header */}
              <div className={css(styles.datePickerMenuHeader)}>
                <div className='fx fx__hv-center'>
                  {/* Year Decrement */}
                  <div
                    className={css(styles.calNavBtn)}
                    onClick={handleYearDecrement}
                  >
                    <i className='fa fa-chevron-left' />
                    <i className='fa fa-chevron-left' />
                  </div>
                  {/* Month Decrement */}
                  <div
                    className={css(styles.calNavBtn)}
                    onClick={handleMonthDecrement}
                  >
                    <i className='fa fa-chevron-left' />
                  </div>
                </div>

                <div className='fx fx__hv-center'>
                  {/* Month header */}
                  <div
                    onClick={() => {
                      setMonthMenuVisibility(!isMonthMenuVisible);
                      setYearMenuVisibility(false);
                    }}
                    className={`noselect ${css(styles.monthHeader, isMonthMenuVisible && styles.activeMonthHeader)}`}
                  >
                    {`${_monthNames[month]}`}
                  </div>
                  {/* Year header */}
                  <div
                    className={`noselect ${css(styles.yearHeader, isYearMenuVisible && styles.activeYearHeader)}`}
                    onClick={() => {
                      setYearMenuVisibility(!isYearMenuVisible);
                      setMonthMenuVisibility(false);
                    }}
                  >
                    {year}
                  </div>
                </div>

                <div className='fx fx__hv-center'>
                  {/* Month Increment */}
                  <div
                    className={css(styles.calNavBtn)}
                    onClick={handleMonthIncrement}
                  >
                    <i className='fa fa-chevron-right' />
                  </div>
                  {/* Year Increment */}
                  <div
                    className={css(styles.calNavBtn)}
                    onClick={handleYearIncrement}
                  >
                    <i className='fa fa-chevron-right' />
                    <i className='fa fa-chevron-right' />
                  </div>
                </div>
              </div>

              {/* Current Menu */}
              {menu}

              {/* Footer */}
              <div
                className={css(styles.datePickerMenuFooter)}
                onClick={handleOnClickToday}
              >
                Today
              </div>
            </div>
          </PopupMenu>
        : null
      }
    </div>
  );
};

DatePicker.propTypes = props;
DatePicker.defaultProps = defaultProps;

export { DatePicker };
